
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Input,
	OnDestroy,
	OnInit,
	QueryList,
	Renderer2,
	ViewChild,
	ViewChildren,
} from '@angular/core';
import { Router } from '@angular/router';

import { Subject } from 'rxjs';
import { TinySliderInfo, TinySliderSettings } from 'tiny-slider/src/tiny-slider';

import { CARD_PICTURE } from 'ib/internetbank-core/ib-constants/size';
import { detectChanges } from 'ib/internetbank-core/ib-helpers/change-detector.helper';
import { IbCoreCommonUtils } from 'ib/internetbank-core/utils/ib-common-utils';


import { ProductTypes } from '../../../../common/models/product-types';
import { TinySliderComponent } from '../../../../ui/addition/tiny-slider/tiny-slider.component';
import { TooltipComponent } from '../../../../ui/controls/tooltip/tooltip.component';
import { CardLabelStatusComponent } from '../../../cards/card-label-status/card-label-status.component';
import { Product } from '../../../products.model';

import { ProductSliderService } from './product-slider.service';

@Component({
	selector: 'app-product-slider',
	templateUrl: './product-slider.component.html',
	styleUrls: ['./product-slider.component.less'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [ProductSliderService],
	standalone: false,
})
export class ProductSliderComponent implements OnInit, OnDestroy {

	@Input()
	public selectedData: Product | undefined;

	@ViewChildren(CardLabelStatusComponent)
	private statuses: QueryList<CardLabelStatusComponent>;

	@ViewChild(TinySliderComponent)
	private tinySlider: TinySliderComponent;


	public isLoaded: boolean = false;

	public tinySliderConfig: TinySliderSettings | undefined;

	public selectedDataIndex: number = 0;

	public products: Array<Product> | undefined;

	public CARD_PICTURE: typeof CARD_PICTURE = CARD_PICTURE;

	private readonly productCount: number = 10;

	private ngUnsubscribe: Subject<void> = new Subject<void>();

	constructor(
		private changeDetectorRef: ChangeDetectorRef,
		public renderer: Renderer2,
		public router: Router,
		private productSliderService: ProductSliderService,
	) { }

	public get isBonus(): boolean {
		return this.selectedData?.productId?.productType === ProductTypes.BONUS;
	}

	public get isCredit(): boolean {
		return this.selectedData?.productId?.productType === ProductTypes.CREDIT;
	}
	public get isAccount(): boolean {
		return this.selectedData?.productId?.productType === ProductTypes.ACCOUNT;
	}

	public ngOnInit(): void {
		this.productSliderService.productList
			.subscribe((products: Array<Product>) => {
				this.isLoaded = true;
				detectChanges(this.changeDetectorRef);

				if (products && products.length) {
					this.handleInitData(products);
				}
			});
	}

	public ngOnDestroy(): void {
		this.ngUnsubscribe.next();
		this.ngUnsubscribe.complete();
	}

	public sliderItemClick = (item: Product): void => {
		this.productSliderService.sliderItemClick(item);
	};

	public handleInitData = (products: Array<Product>): void => {
		this.setProducts(products);
		this.selectedData = this.products?.[this.selectedDataIndex];


		if (this.products && this.products.length) {
			this.buildSlider();
		}
	};

	public setProducts(products: Array<Product>): void {
		this.products = this.productSliderService.filteredProducts(products);
		detectChanges(this.changeDetectorRef);
	}

	public initHandle = (): void => {
		detectChanges(this.changeDetectorRef);
		this.tinySlider.slider?.events.on('indexChanged', this.indexChanged);
		this.tinySlider.slider?.events.on('touchStart', this.handleTouchStart);

		const info: TinySliderInfo | undefined = this.tinySlider?.slider?.getInfo();
		this.indexChanged(info);

		detectChanges(this.changeDetectorRef);
	};


	public handleTouchStart = (): void => {
		this.statuses.forEach((element: CardLabelStatusComponent) => {
			const tooltip: TooltipComponent = element.tooltip;
			if (tooltip) {
				tooltip.hideHover();
			}
		});
		detectChanges(this.changeDetectorRef);
	};

	public indexChanged = (info: TinySliderInfo | undefined): void => {
		let index: number = info?.index || 0;
		const prevSlide: HTMLElement = info?.slideItems[info.indexCached] as HTMLElement;
		const curentSlide: HTMLElement = info?.slideItems[index] as HTMLElement;
		if (prevSlide) {
			prevSlide.classList.remove('selected');
		}
		if (curentSlide) {
			curentSlide.classList.add('selected');
			const dataIndex = curentSlide.getAttribute('data-index');
			if (dataIndex) {
				index = +dataIndex;
				this.selectItem(index);
			}
		}
	};

	public selectItem = (index: number): void => {
		this.selectedData = this.products?.[index];
		this.selectedDataIndex = index;
		detectChanges(this.changeDetectorRef);
	};

	public isHasNavigation(): boolean {
		return !IbCoreCommonUtils.numberIsEmpty(this.selectedDataIndex) && (this.products?.length || 0) > this.productCount;
	}

	private buildSlider(): void {
		const maxViewPortItemsCount: number = 1;
		const itemsLength: number = ((this.products?.length || 0) === 1 || (this.products?.length || 0) === 2) ? 1 : maxViewPortItemsCount;
		const isLoopOptionActive: boolean = (this.products?.length || 0) > maxViewPortItemsCount;
		const hasNavigation: boolean = (this.products?.length || 0) > 1 && ((this.products?.length || 0) <= this.productCount);

		this.tinySliderConfig = undefined;
		detectChanges(this.changeDetectorRef);
		this.tinySliderConfig = {
			items: itemsLength,
			nav: hasNavigation,
			navAsThumbnails: true,
			loop: isLoopOptionActive,
			autoplay: false,
			speed: 500,
			autoplayButtonOutput: false,
			mouseDrag: true,
			freezable: false,
			controls: false,
			onInit: this.initHandle,
			autoWidth: false,
		};
		detectChanges(this.changeDetectorRef);
	}

}
